import React, { useEffect, useState } from "react";
import {
  Database,
  Cloud,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Zap,
  Image as ImageIcon,
  FileText,
  Server,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const Pill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
      ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
    }`}
  >
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {label}
  </span>
);

const UsageBar = ({ used, limit, tone = "sky" }) => {
  const pct = Math.min(100, limit ? (used / limit) * 100 : 0);
  const bar = pct > 85 ? "bg-rose-500" : pct > 60 ? "bg-amber-500" : `bg-${tone}-500`;
  return (
    <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1">
      <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const fmtMB = (mb) => {
  if (!mb) return "0 MB";
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
};

/**
 * SystemHealthWidget — compact admin-only panel on the Dashboard.
 * Shows green/red health badges for Atlas + R2 plus storage usage
 * and capacity projections (what happens with 4000 budgets, 200 schools).
 */
export default function SystemHealthWidget({ tile = false, defaultOpen = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/system/health", { silent: true });
        setData(r.data);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!data) return null;

  const atlas = data.atlas || {};
  const r2 = data.r2 || {};
  const proj = data.projection || {};
  const atlasPct = atlas.limit_mb ? (atlas.data_size_mb / atlas.limit_mb) * 100 : 0;
  const r2Pct = r2.limit_gb ? ((r2.used_mb || 0) / 1024 / r2.limit_gb) * 100 : 0;

  if (tile) {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} data-testid="system-health-tile"
          className="w-full h-full min-w-0 flex flex-col items-start gap-1 p-2.5 rounded-xl bg-slate-900 text-white card-shadow hover:brightness-110 active:scale-[0.98] transition-[filter,transform]">
          <Server className="w-4 h-4" />
          <span className="font-heading text-[11.5px] font-black leading-tight">Infra</span>
          <span className="text-[9.5px] leading-tight text-white/80">{atlas.ok && r2.ok ? "Tudo online" : "Verificar"}</span>
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-3 pt-10">
            <DialogTitle className="sr-only">Infraestrutura</DialogTitle>
            <SystemHealthWidget defaultOpen />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-3 card-shadow"
      data-testid="system-health-widget"
    >
      {/* Compact header — always visible */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-1.5 flex items-center gap-2 hover:bg-slate-50 transition-colors"
        data-testid="system-health-toggle"
      >
        <div className="w-6 h-6 rounded-md bg-slate-900 flex items-center justify-center shrink-0">
          <Server className="w-3 h-3 text-white" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="font-heading text-[12px] font-black text-slate-900 leading-tight">
            Infraestrutura
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Pill ok={atlas.ok} label="Atlas" />
          <Pill ok={r2.ok} label="R2" />
          <ChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4 space-y-4">
          {/* Atlas card */}
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-emerald-700" />
              <p className="text-[12.5px] font-black text-slate-900">MongoDB Atlas</p>
              <Pill ok={atlas.ok} label={atlas.ok ? "Online" : "Offline"} />
              {atlas.latency_ms != null && (
                <span className="text-[10px] text-slate-500 ml-auto">
                  <Zap className="w-3 h-3 inline mr-0.5" />
                  {atlas.latency_ms} ms
                </span>
              )}
            </div>
            <div className="mt-2">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-600">Dados usados</span>
                <span className="font-black text-slate-900 tabular-nums">
                  {fmtMB(atlas.data_size_mb)} / {fmtMB(atlas.limit_mb)}
                </span>
              </div>
              <UsageBar used={atlas.data_size_mb} limit={atlas.limit_mb} tone="emerald" />
              <p className="text-[10px] text-slate-500 mt-1">
                {atlasPct.toFixed(1)}% da sua cota • {atlas.docs?.toLocaleString("pt-BR")} documentos em{" "}
                {atlas.collections} coleções
              </p>
            </div>
            {/* Per-collection */}
            {atlas.by_collection && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {Object.entries(atlas.by_collection).map(([k, v]) => (
                  <div
                    key={k}
                    className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-1.5"
                  >
                    <p className="text-[9px] uppercase tracking-widest text-slate-500 font-black">
                      {k}
                    </p>
                    <p className="text-[12px] font-black text-slate-900 tabular-nums">
                      {Number(v).toLocaleString("pt-BR")}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* R2 card */}
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4 text-sky-700" />
              <p className="text-[12.5px] font-black text-slate-900">
                Cloudflare R2 {r2.bucket && <span className="text-slate-400 font-normal">• {r2.bucket}</span>}
              </p>
              <Pill ok={r2.ok} label={r2.ok ? "Online" : r2.enabled ? "Erro" : "Desabilitado"} />
              {r2.latency_ms != null && (
                <span className="text-[10px] text-slate-500 ml-auto">
                  <Zap className="w-3 h-3 inline mr-0.5" />
                  {r2.latency_ms} ms
                </span>
              )}
            </div>
            {r2.ok && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-600">Armazenamento</span>
                  <span className="font-black text-slate-900 tabular-nums">
                    {(r2.used_mb || 0).toFixed(1)} MB / {r2.limit_gb} GB
                  </span>
                </div>
                <UsageBar used={(r2.used_mb || 0) / 1024} limit={r2.limit_gb} tone="sky" />
                <p className="text-[10px] text-slate-500 mt-1">
                  {r2Pct.toFixed(2)}% da sua cota • {r2.objects?.toLocaleString("pt-BR")} arquivos
                  {r2.estimate_capped && (
                    <span className="ml-1 text-amber-600">(estimativa parcial)</span>
                  )}
                </p>
              </div>
            )}
            {!r2.ok && r2.error && (
              <p className="mt-2 text-[11px] text-rose-700">{r2.error}</p>
            )}
          </div>

          {/* Projections */}
          {Object.keys(proj).length > 0 && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-sky-700" />
                <p className="text-[12px] font-black text-sky-900">Projeção de crescimento</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-[11.5px]">
                <div className="flex items-start gap-2 bg-white border border-slate-100 rounded-lg p-2">
                  <FileText className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-black text-slate-900">4.000 orçamentos</p>
                    <p className="text-slate-600">
                      Atlas ≈ <strong>{fmtMB(proj.atlas_at_4000_budgets_mb)}</strong>
                      <span className="text-slate-400"> • cabem em {atlas.limit_mb} MB? </span>
                      {proj.atlas_at_4000_budgets_mb < atlas.limit_mb ? (
                        <span className="text-emerald-700 font-black">Sim ✓</span>
                      ) : (
                        <span className="text-amber-700 font-black">Perto do limite</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-white border border-slate-100 rounded-lg p-2">
                  <ImageIcon className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-black text-slate-900">4.000 × 10 fotos</p>
                    <p className="text-slate-600">
                      R2 ≈ <strong>{proj.r2_at_4000_budgets_gb} GB</strong>
                      <span className="text-slate-400"> • cota {r2.limit_gb} GB</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-white border border-slate-100 rounded-lg p-2">
                  <ImageIcon className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-black text-slate-900">200 escolas × 20 fotos</p>
                    <p className="text-slate-600">
                      R2 ≈ <strong>{proj.r2_at_200_schools_photos_gb} GB</strong>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-white border border-slate-100 rounded-lg p-2">
                  <Zap className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-black text-slate-900">Médias atuais</p>
                    <p className="text-slate-600">
                      {proj.avg_doc_kb} KB por doc • {proj.avg_photo_mb} MB por foto
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-2 leading-snug">
                Projeções baseadas nas médias atuais do seu banco. Se precisar expandir a
                cota, dá para fazer upgrade do plano no Atlas e no R2 sem downtime.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
